import React from "react";

/**
 * Interface for the CartIcon component props.
 * @interface CartIconProps
 * @property {string} [className] - Optional CSS class name for styling the SVG
 * @property {string} [color] - Optional color for the cart icon (default: 'currentColor')
 * @property {string} [size] - Optional size for the icon (default: '1.5rem')
 * @property {number} [strokeWidth] - Optional stroke width for the icon (default: 16)
 */
interface CartIconProps {
  className?: string;
  color?: string;
  size?: string;
  strokeWidth?: number;
}

/**
 * A customizable shopping cart icon component.
 * Used for shopping cart indicators, checkout buttons, or other shopping-related UI elements.
 *
 * @component
 * @example
 * // Cart icon with default color and size
 * <CartIcon />
 *
 * @example
 * // Cart icon with custom color, size, and stroke width
 * <CartIcon
 *   color="#FF0000"
 *   size="1.5rem"
 *   className="cart-icon"
 *   strokeWidth={12}
 * />
 *
 * @param {CartIconProps} props - Component props
 * @param {string} [props.className=''] - CSS class name for additional styling
 * @param {string} [props.color='currentColor'] - Stroke color of the icon
 * @param {string} [props.size='1.5rem'] - Size of the icon
 * @param {number} [props.strokeWidth=16] - Width of the stroke
 * @returns {JSX.Element} SVG cart icon
 */
const CartIcon: React.FC<CartIconProps> = ({
  className = "",
  color = "currentColor",
  size = "1.5rem",
  strokeWidth = 16,
}) => {
  return (
    <svg
      viewBox="0 0 256 256"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{
        width: size,
        height: size,
        display: "inline-block",
        verticalAlign: "middle",
      }}
    >
      <rect fill="none" height="256" width="256" />
      <path
        d="M184,184H69.8L41.9,30.6A8,8,0,0,0,34.1,24H16"
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
      <circle
        cx="80"
        cy="204"
        fill="none"
        r="20"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
      <circle
        cx="184"
        cy="204"
        fill="none"
        r="20"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
      <path
        d="M62.5,144H188.1a15.9,15.9,0,0,0,15.7-13.1L216,64H48"
        fill="none"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={strokeWidth}
      />
    </svg>
  );
};

export default CartIcon;
